import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { categoryMediaApi, CategoryImageUpload } from './categoryMediaApi';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function useCatalogCategoryMedia(categoryId: number) {
  const client = useQueryClient();
  const queryKey = ['catalog', 'category-image', categoryId];
  const query = useQuery({
    queryKey,
    queryFn: () => categoryMediaApi.get(categoryId),
    refetchInterval: (query) => {
      const photo = query.state.data?.image;
      const isProcessing = photo && (photo.link_image_is_published === false || photo.link_pending_file_id != null);
      return isProcessing && photo.link_processing_status !== 'failed' ? 2000 : false;
    },
  });
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const retryRequest = useRef<CategoryImageUpload | undefined>(undefined);
  const photo = query.data?.image;

  const run = async (operation: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await operation();
      await client.invalidateQueries({ queryKey });
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : 'Не удалось изменить картинку.');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (request: CategoryImageUpload): Promise<void> => {
    await categoryMediaApi.upload(categoryId, request);
    retryRequest.current = undefined;
  };

  const select = (files: File[]): void => {
    const file = files[0];
    if (!file) return;
    const isSupported = IMAGE_MIME_TYPES.includes(file.type);
    const isWithinLimit = file.size <= MAX_IMAGE_BYTES;
    const isOk = isSupported && isWithinLimit;
    if (!isOk) {
      setError('Выберите JPG, PNG или WebP размером до 10 МБ.');
      return;
    }

    const request = { file, idempotencyKey: crypto.randomUUID(), linkId: photo?.link_id, revision: photo?.link_image_revision ?? 0 };
    retryRequest.current = request;
    void run(() => upload(request));
  };

  const retry = (): void => {
    const request = retryRequest.current;
    if (request) void run(() => upload(request));
  };

  const remove = (): void => {
    if (!photo) return;
    void run(async () => {
      await categoryMediaApi.remove(categoryId, photo.link_id);
      retryRequest.current = undefined;
    });
  };

  return { query, photo, isBusy, error, canRetry: retryRequest.current !== undefined, select, retry, remove };
}
